import React from "react";
import { Star, BadgeCheck } from "lucide-react";

const AVATARS = [
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=96&h=96&fit=crop&crop=faces&q=80",
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=96&h=96&fit=crop&crop=faces&q=80",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=96&h=96&fit=crop&crop=faces&q=80",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=96&h=96&fit=crop&crop=faces&q=80",
  "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&w=96&h=96&fit=crop",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=96&h=96&fit=crop&crop=faces&q=80",
  "https://images.pexels.com/photos/15780889/pexels-photo-15780889.jpeg?auto=compress&cs=tinysrgb&w=96&h=96&fit=crop",
  "https://images.unsplash.com/photo-1705645930353-0e335311ef20?w=96&h=96&fit=crop&crop=faces&q=80",
  "https://images.pexels.com/photos/7468194/pexels-photo-7468194.jpeg?auto=compress&cs=tinysrgb&w=96&h=96&fit=crop",
  "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&w=96&h=96&fit=crop",
];

const reviews = [
  {
    name: "Mariana S.",
    role: "Marketing Manager",
    location: "Lisbon, PT",
    stars: 5,
    text: "I uploaded my old CV at lunch and had a completely rewritten version before my coffee went cold. Two weeks later I had three interview calls. The ATS score jump was real.",
  },
  {
    name: "Diego R.",
    role: "Software Engineer",
    location: "Madrid, ES",
    stars: 5,
    text: "As an engineer I was skeptical of an AI writing my resume. It didn't invent a single thing — it just made my actual work sound as strong as it really was. Impressive.",
  },
  {
    name: "Ana P.",
    role: "Business Graduate",
    location: "São Paulo, BR",
    stars: 4,
    text: "The design is clean and the wording is so much stronger than what I had. I only wish I could pick more than one language per order — but the Portuguese output was perfect.",
  },
  {
    name: "James T.",
    role: "Data Analyst",
    location: "London, UK",
    stars: 5,
    text: "The analysis report alone was worth it — it showed exactly why my CV was being filtered out. The rewrite fixed every issue. Got past the ATS at a company that rejected me twice before.",
  },
  {
    name: "Sofia L.",
    role: "Product Designer",
    location: "Porto, PT",
    stars: 5,
    text: "The cover letter matched my resume perfectly and sounded like me on my best day. Recruiters started replying within days. Easiest money I've spent on my career.",
  },
  {
    name: "Carlos M.",
    role: "Sales Director",
    location: "Mexico City, MX",
    stars: 4,
    text: "Fast, professional, and the result reads like a $500 resume writer did it. I used one revision to adjust the tone and the second version was spot on.",
  },
  {
    name: "Emma K.",
    role: "Nurse",
    location: "Dublin, IE",
    stars: 5,
    text: "English isn't my first language and my old CV showed it. The new one is flawless — confident, natural, professional. I finally feel proud sending it out.",
  },
  {
    name: "Rui A.",
    role: "Civil Engineer",
    location: "Luanda, AO",
    stars: 4,
    text: "Very good experience overall. The AI caught weak points I hadn't noticed in ten years of using the same CV. Delivery was literally minutes after payment.",
  },
  {
    name: "Julia F.",
    role: "HR Specialist",
    location: "Berlin, DE",
    stars: 5,
    text: "I screen resumes for a living, so trust me when I say: this is what recruiters want to see. Clear structure, strong verbs, real impact. My own CV finally follows my own advice.",
  },
  {
    name: "Miguel O.",
    role: "Financial Analyst",
    location: "Miami, US",
    stars: 5,
    text: "Paid, uploaded, done — my new resume was ready before I finished reading the confirmation email. The before/after score made the value obvious instantly.",
  },
];

reviews.forEach((r, i) => {
  r.avatarIndex = i;
});

const Stars = ({ count, testId }) => (  <div className="flex items-center gap-0.5" data-testid={testId} aria-label={`${count} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`h-3.5 w-3.5 ${i <= count ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`}
      />
    ))}
  </div>
);

const initials = (name) =>
  name
    .replace(".", "")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const ReviewCard = ({ review, index, rowPrefix }) => (
  <article
    className="relative w-[320px] sm:w-[380px] shrink-0 rounded-2xl border border-white/10 bg-zinc-950/70 p-6 hover:border-white/20 hover:bg-zinc-950/90 transition-colors duration-300"
    data-testid={`${rowPrefix}-review-card-${index}`}
  >
    <div className="flex items-center justify-between">
      <Stars count={review.stars} testId={`${rowPrefix}-review-stars-${index}`} />
      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400/80">
        <BadgeCheck className="h-3 w-3" />
        Verified order
      </span>
    </div>
    <p
      className="mt-4 text-sm leading-relaxed text-zinc-200 min-h-[7rem]"
      data-testid={`${rowPrefix}-review-text-${index}`}
    >
      &ldquo;{review.text}&rdquo;
    </p>
    <div className="mt-5 flex items-center gap-3 pt-4 border-t border-white/5">
      <span
        aria-hidden
        className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-500/40 to-purple-500/40 ring-1 ring-white/10 text-xs font-semibold text-white"
      >
        {initials(review.name)}
        <img
          src={AVATARS[review.avatarIndex]}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </span>
      <div>
        <p className="text-sm font-medium text-white leading-tight" data-testid={`${rowPrefix}-review-name-${index}`}>
          {review.name}
        </p>
        <p className="text-xs text-zinc-500 mt-0.5">
          {review.role} · {review.location}
        </p>
      </div>
    </div>
  </article>
);

export const Testimonials = () => {
  const rowA = reviews.slice(0, 5);
  const rowB = reviews.slice(5);

  return (
    <section id="testimonials" className="relative py-24 sm:py-32 overflow-hidden" data-testid="testimonials-section">
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-1/3 h-[360px] w-[720px] rounded-full bg-gradient-to-r from-blue-600/10 to-purple-600/10 blur-[140px] pointer-events-none"
      />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Reviews</p>
          <h2
            className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
            data-testid="testimonials-headline"
          >
            Job seekers land interviews with CV AI PRO.
          </h2>
          <div className="mt-4 flex items-center justify-center gap-2" data-testid="reviews-aggregate">
            <Stars count={5} testId="reviews-aggregate-stars" />
            <span className="text-sm text-zinc-400">
              <span className="text-white font-medium">4.8/5</span> from 120+ resumes delivered
            </span>
          </div>
        </div>
      </div>

      {/* Rolling rows — full bleed */}
      <div className="relative mt-14 space-y-5" data-testid="reviews-marquee">
        <div className="review-marquee">
          <div className="review-marquee-track" data-testid="reviews-row-a">
            {[...rowA, ...rowA].map((r, i) => (
              <ReviewCard key={`a-${i}`} review={r} index={i % rowA.length} rowPrefix="rowa" />
            ))}
          </div>
        </div>
        <div className="review-marquee">
          <div className="review-marquee-track reverse" data-testid="reviews-row-b">
            {[...rowB, ...rowB].map((r, i) => (
              <ReviewCard key={`b-${i}`} review={r} index={i % rowB.length} rowPrefix="rowb" />
            ))}
          </div>
        </div>
      </div>

      <p className="mt-10 text-center text-[11px] text-zinc-500" data-testid="testimonials-disclaimer">
        Individual results may vary. We do not guarantee interviews or employment.
      </p>
    </section>
  );
};
